import { AlertTriangle, FileQuestion } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { EmptyState } from '@/components/shared/EmptyState';
import { PolicyDeniedView } from '@/components/common/PolicyDeniedView';
import { Button } from '@/components/ui/button';
import { normalizeApiError } from '@/features/common/apiError';

export interface QueryErrorViewProps {
  /** RTK Query error — `unknown` because the shell apiSlice types its baseQuery
   * as a plain `BaseQueryFn` (no error generic). `normalizeApiError` reads it. */
  error: unknown;
  /** What failed to load, for the "Couldn't load {things}" title (design §16.3). */
  label: string;
  onRetry?: () => void;
}

/**
 * One error view for every failed GET: 403 → `PolicyDeniedView`, 404 → the
 * "couldn't find it" empty state with a way back, anything else → retry.
 */
export function QueryErrorView({ error, label, onRetry }: QueryErrorViewProps) {
  const navigate = useNavigate();
  const normalized = normalizeApiError(error);

  if (normalized.status === 403) {
    return <PolicyDeniedView />;
  }

  if (normalized.status === 404) {
    return (
      <EmptyState
        icon={<FileQuestion />}
        title="We couldn't find what you're looking for"
        description="It may have been deleted."
        action={
          <Button variant="outline" onClick={() => navigate(-1)}>
            Go back
          </Button>
        }
      />
    );
  }

  return (
    <EmptyState
      icon={<AlertTriangle />}
      title={`Couldn't load ${label}`}
      description="We couldn't load this right now. Please try again."
      action={
        onRetry ? (
          <Button variant="outline" onClick={onRetry}>
            Try again
          </Button>
        ) : undefined
      }
    />
  );
}
